import { Image } from './types'
import { ImagePreloadLink } from '../../../../common/types'
import {
    SMALL_MOBILE_IMAGE_RESIZE_WIDTH,
    MEDIUM_MOBILE_IMAGE_RESIZE_WIDTH,
    LARGE_MOBILE_IMAGE_RESIZE_WIDTH,
    DESKTOP_IMAGE_RESIZE_WIDTH,
    WIDE_SCREEN_IMAGE_RESIZE_WIDTH,
} from '../../../../common/image-widths'

export const STICKY_FOOTER_SCROLL_THRESHOLD = 100

export const isVideo = (image: Image): boolean => {
    return image?.src?.includes('.m3u8') ?? false
}

export const getImageUrl = (format: string, image: Image) => {
    const { src, thumbnail } = image || {}
    const _getResizedImageUrl = (width: number) => getResizedImageUrl(isVideo(image) ? thumbnail : src, width)
    switch (format) {
        case 'small-mobile': {
            return _getResizedImageUrl(SMALL_MOBILE_IMAGE_RESIZE_WIDTH)
        }
        case 'medium-mobile': {
            return _getResizedImageUrl(MEDIUM_MOBILE_IMAGE_RESIZE_WIDTH)
        }
        case 'large-mobile': {
            return _getResizedImageUrl(LARGE_MOBILE_IMAGE_RESIZE_WIDTH)
        }
        case 'desktop': {
            return _getResizedImageUrl(DESKTOP_IMAGE_RESIZE_WIDTH)
        }
        default: {
            return _getResizedImageUrl(WIDE_SCREEN_IMAGE_RESIZE_WIDTH)
        }
    }
}

export const getResizedImageUrl = (imageUrl: string | undefined, width: number) => {
    if (!imageUrl) {
        return null
    }
    try {
        const url = new URL(imageUrl)
        url.searchParams.set('imwidth', width.toString())
        return url.toString()
    } catch {
        // Relative /media URL: new URL() throws without a base, so append the param manually.
        const separator = imageUrl.includes('?') ? '&' : '?'
        return `${imageUrl}${separator}imwidth=${width}`
    }
}

// Media ranges mirror the <source> breakpoints in gallery-carousel.tsx / main-image-component.tsx
// — keep them in sync, or the preload won't match what's actually rendered.
export const generateGalleryImagePreloadLinks = (image: Image): ImagePreloadLink[] => {
    const breakpoints: { format: string; media: string }[] = [
        { format: 'small-mobile', media: '(max-width: 319px)' },
        { format: 'medium-mobile', media: '(min-width: 320px) and (max-width: 479px)' },
        { format: 'large-mobile', media: '(min-width: 480px) and (max-width: 639px)' },
        { format: 'desktop', media: '(min-width: 640px) and (max-width: 1023px)' },
        { format: 'wide-screen', media: '(min-width: 1024px)' },
    ]

    return breakpoints.reduce<ImagePreloadLink[]>((links, { format, media }) => {
        const href = getImageUrl(format, image)
        if (href) {
            links.push({ href, media, fetchPriority: 'high' })
        }
        return links
    }, [])
}
